/**
 * =============================================================================
 *  BANG THONG TIN NOI - bang thuoc tinh + danh sach doi tuong, sau MOT nut bam
 * =============================================================================
 *
 *  Truoc day day la mot cot co dinh ben phai, roi thanh mot lop noi co HAI the:
 *  "Đối tượng" va "Chẩn đoán". Nguoi dung noi bang CHAN DOAN (vi sao khoi chu sua
 *  duoc / khong sua duoc) la khong can thiet - da bo han. O day chi con:
 *    - BANG THUOC TINH cua thu dang sua / dang chon (phong, co chu, dam/nghieng,
 *      can le, mau...) - kem canh bao thay font - hien o DAU lop noi.
 *    - DANH SACH DOI TUONG tren trang (chon, doi thu tu chong lop, xoa).
 *
 *  Trang thai dong / mo nam trong kho trang thai (AppState.inspectorOpen) chu
 *  KHONG trong thanh phan nay: doi cong cu se ve lai cay React, va mot trang thai
 *  cuc bo o day se lang le dong bang ma nguoi dung vua co y mo ra.
 *
 * =============================================================================
 *  CANH BAO THAY FONT KHONG NAM RIENG O DAY
 * =============================================================================
 *  Canh bao "hình thức tài liệu sẽ thay đổi" van hien ngay trong bang phan hoi
 *  cua trinh soan tai cho (EditFeedback trong bang thuoc tinh o tren), tuc la
 *  ngay canh o dang go, khong the bo lo - khong phu thuoc bang chan doan da bo.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { ObjectPanel, type ObjectPanelProps } from './ObjectPanel';

/**
 * Thuoc tinh cua NUT MO bang. Cu bam ra ngoai de dong phai bo qua chinh cai nut
 * da mo no, neu khong thi: pointerdown dong bang -> click tren nut mo lai bang,
 * va nut do khong bao gio dong duoc gi.
 */
export const INSPECTOR_TRIGGER_ATTR = 'data-inspector-trigger';

export interface InspectorPopoverProps {
  onClose: () => void;
  objects: ObjectPanelProps;
  /**
   * Dang sua chu khong. Khi dang sua, bang nay CHINH LA bang tuy chon cua o dang
   * sua, nen KHONG dong khi bam ra ngoai (bam vao o chu / o go nam ngoai bang).
   * Van dong duoc bang nut ✕, nut Thông tin, hoac Esc.
   */
  editing: boolean;
  /**
   * BANG THUOC TINH cua thu dang sua / dang chon (kieu chu, mau, do mo, canh bao
   * thay font...). Hien o DAU lop noi, luon thay duoc. Bo qua = khong co gi dang
   * sua / chon.
   */
  properties?: ReactNode;
}

export function InspectorPopover({ onClose, objects, editing, properties }: InspectorPopoverProps) {
  const boxRef = useRef<HTMLDivElement | null>(null);

  // Bam ra ngoai thi dong - TRU KHI DANG SUA CHU. Trong luc sua, bang nay chinh la
  // bang tuy chon cua o dang sua: bam vao o chu (de sua) hay vao o go deu nam
  // NGOAI bang, nen dong theo cu bam ra ngoai la bat cong cu / bam o mot cai la
  // bang bien mat ngay ("focus vào text area thì popup phải nổi lên"). Luc dang
  // sua van dong duoc bang nut ✕, nut Thông tin, hoac Esc.
  useEffect(() => {
    if (editing) return;
    const onDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (boxRef.current?.contains(target)) return;
      if (target.closest(`[${INSPECTOR_TRIGGER_ATTR}]`)) return;
      onClose();
    };
    window.addEventListener('pointerdown', onDown);
    return () => window.removeEventListener('pointerdown', onDown);
  }, [onClose, editing]);

  // Escape KHONG duoc xu ly o day: phim tat toan cuc trong App.tsx da doc
  // inspectorOpen va dong bang truoc moi viec khac. Hai duong dong bang song song
  // se doi hoi hai lan bam Escape trong dung mot khung hinh nao do.

  return (
    <div className="inspectorPopover" role="dialog" aria-label="Thông tin đối tượng" ref={boxRef}>
      {properties && <div className="inspectorProperties">{properties}</div>}

      {/* Header chi con nut dong: nhan "Đối tượng" + so luong da nam trong chinh
          bang ObjectPanel ben duoi, khong lap lai o day. */}
      <div className="inspectorPopoverHead">
        <button
          type="button"
          className="iconButton tiny"
          onClick={onClose}
          title="Đóng bảng thông tin (Esc)"
          aria-label="Đóng bảng thông tin"
        >
          ✕
        </button>
      </div>

      <div className="inspectorPopoverBody">
        <ObjectPanel {...objects} />
      </div>
    </div>
  );
}
